/**
 * Per-user permission override panel.
 *
 * The join-table backend (avrio-backend#108/#110) has existed since
 * 2026-09-18; this drawer was left as a disabled preview until now. Every
 * switch here writes for real: PUT /api/users/{userId}/permissions/{permission}
 * to override, DELETE on the same path to clear the override and revert to
 * the role default -- toggling a switch back to what the role already grants
 * clears rather than sets, so `overrideCount` (and the badge next to it)
 * only ever counts real customization, not switches that happen to match
 * the default.
 *
 * Renders off `roleCapabilityGroups`/`CAPABILITY_GROUPS`, the same source
 * the role cards and the override badge use -- not a second, hand-maintained
 * list of the same ~15 rows. A row's checked state comes from the live
 * `effectivePermissions` list once loaded (role defaults merged with this
 * user's real overrides), not from the static role table, so a user who
 * already has overrides sees their actual access, not their role's.
 */

import { memo, useEffect, useState } from 'react';
import { Drawer, Alert, Typography, Switch, Space, Spin } from 'antd';
import axios from '../../../../axiosConfig';
import {
  CAPABILITY_GROUPS,
  type CapabilityGroupKey,
  type CapabilityGroupItem,
} from '../../utils/roleCapabilities';
import {
  permissions as roleDefaultPermissions,
  UserRole,
} from '../../utils/role';
import styles from './index.module.css';

const { Title, Text } = Typography;

export interface CustomizePermissionsDrawerProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  userName: string;
  role: UserRole;
}

interface UserPermissionOverride {
  permission: string;
  granted: boolean;
}

interface UserPermissionsResponse {
  userId: number;
  role: string;
  roleDefaultPermissions: string[];
  effectivePermissions: string[];
  overrides: UserPermissionOverride[];
}

const GROUP_TITLES: Record<CapabilityGroupKey, string> = {
  canDo: 'Can do',
  canSee: 'Can see',
};

export const CustomizePermissionsDrawer = memo(
  ({
    open,
    onClose,
    userId,
    userName,
    role,
  }: CustomizePermissionsDrawerProps) => {
    const [data, setData] = useState<UserPermissionsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savingKey, setSavingKey] = useState<string | null>(null);

    useEffect(() => {
      if (!open) {
        setData(null);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      axios
        .get<UserPermissionsResponse>(`/api/users/${userId}/permissions`)
        .then((res) => setData(res.data))
        .catch(() => setError('Could not load this user’s permissions.'))
        .finally(() => setLoading(false));
    }, [open, userId]);

    const handleToggle = (row: CapabilityGroupItem, nextGranted: boolean) => {
      setSavingKey(row.key);
      setError(null);
      const requests = row.permissions.map((permission) => {
        const isRoleDefault =
          roleDefaultPermissions[role].includes(permission) === nextGranted;
        return isRoleDefault
          ? axios.delete<UserPermissionsResponse>(
              `/api/users/${userId}/permissions/${permission}`,
            )
          : axios.put<UserPermissionsResponse>(
              `/api/users/${userId}/permissions/${permission}`,
              { granted: nextGranted },
            );
      });
      Promise.all(requests)
        .then((responses) => setData(responses[responses.length - 1].data))
        .catch(() => setError('Could not update this permission. Try again.'))
        .finally(() => setSavingKey(null));
    };

    const rows = data
      ? CAPABILITY_GROUPS.map((item) => ({
          ...item,
          granted: item.permissions.every((p) =>
            data.effectivePermissions.includes(p),
          ),
        }))
      : [];

    return (
      <Drawer
        title={`Customize permissions for ${userName}`}
        open={open}
        onClose={onClose}
        width={420}
      >
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            className={styles.banner}
          />
        )}
        {loading && !data && (
          <div className={styles.loading}>
            <Spin />
          </div>
        )}
        {data && (
          <>
            <Alert
              type="info"
              showIcon
              message="Overrides apply to this person only"
              description={`Switched off from ${role}'s default, or on beyond it. Reverting a switch to the role's own default clears the override.`}
              className={styles.banner}
            />
            {(['canDo', 'canSee'] as CapabilityGroupKey[]).map((group) => (
              <div key={group} className={styles.group}>
                <Title level={5} className={styles.groupTitle}>
                  {GROUP_TITLES[group]}
                </Title>
                <Space direction="vertical" size={12} className={styles.rows}>
                  {rows
                    .filter((row) => row.group === group)
                    .map((row) => (
                      <div key={row.key} className={styles.row}>
                        <Text>{row.label}</Text>
                        <Switch
                          checked={row.granted}
                          loading={savingKey === row.key}
                          disabled={savingKey !== null && savingKey !== row.key}
                          onChange={(checked) => handleToggle(row, checked)}
                        />
                      </div>
                    ))}
                </Space>
              </div>
            ))}
          </>
        )}
      </Drawer>
    );
  },
);

CustomizePermissionsDrawer.displayName = 'CustomizePermissionsDrawer';

export default CustomizePermissionsDrawer;
